import GateRevealPanel from './GateRevealPanel';
import { getPanelRevealTitle } from '../utils/gatePresentation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import ComponentControls from './ComponentControls';
import GateBadge from './GateBadge';
import AdCreativeRenderer from './AdCreativeRenderer';
import { useGateAccess } from '../hooks/useGateAccess';
import { db } from '../firebaseConfig';
import { buildPanelClassName } from '../utils/panelTheme';
import { AD_CAMPAIGNS_COLLECTION, AD_PLACEMENTS_COLLECTION, isAdMediumAllowed, isAdCampaignServing, isRenderableAdPlacementStatus, normalizeAdCreative } from '../utils/adMarketplace';
import { callAdMarketplace, recordAdEvent } from '../utils/adMarketplaceClient';

export default function AdComponent({
                                        comp,
                                        index,
                                        moveUp,
                                        moveDown,
                                        onEdit,
                                        isEditor = false,
                                        removeComponent,
                                        onGateTypeChange,
                                        onGateListChange,
                                        extraGates,
                                        gateLogic,
                                        onExtraGatesChange,
                                        onGateLogicChange,
                                        donationCampaignOptions = [],
                                        donationGate,
                                        onDonationGateChange,
                                        onDirectPaymentGateChange,
                                        onDirectPaymentDraftChange,
                                        style = {},
                                        onStyleChange,
                                        coverUrl = '',
                                        onCoverChange,
                                        onPanelBackgroundImageChange,
                                        ownerPublicKey = '',
                                    }) {
    const ownerPublicKeyStr = String(ownerPublicKey || comp?.ownerUid || comp?.ownerPublicKey || '').trim();
    const gate = useMemo(
        () => comp.gate || { type: 'public', listId: '' },
        [comp.gate]
    );
    const gates = useMemo(() => [gate, ...(Array.isArray(comp.extraGates) ? comp.extraGates : [])], [gate, comp.extraGates]);
    const logic = comp.gateLogic === 'all' ? 'all' : 'any';
    const donationMap = useMemo(() => {
        const m = new Map();
        (donationCampaignOptions || []).forEach((option) => {
            if (option?.id) {
                m.set(String(option.id), {
                    campaignName: option.campaignName || '',
                    currency: option.currency || 'SOL',
                    tokenMint: option.tokenMint || '',
                });
            }
        });
        return m;
    }, [donationCampaignOptions]);
    const [hasAccess, isLoading, gateNamesLabel, , gateAccess] = useGateAccess(
        { gates, logic, donationLookup: donationMap },
        { spaceOwnerUid: ownerPublicKeyStr, isEditor, componentId: comp.id }
    );
    const { resetPasswordAccess, resetPasswordError } = gateAccess;
    const [placement, setPlacement] = useState(null);
    const [campaign, setCampaign] = useState(null);
    const [loadingAd, setLoadingAd] = useState(false);
    const [adError, setAdError] = useState('');
    const [now, setNow] = useState(Date.now);
    const [assetReady, setAssetReady] = useState(false);
    const adElement = useRef(null);
    const [adMounted, setAdMounted] = useState(false);
    const attachAd = useCallback(node => { adElement.current = node; setAdMounted(!!node); }, []);
    const visible = useRef(false);
    const ready = useCallback(() => setAssetReady(true), []);
    const assetError = useCallback(() => setAssetReady(false), []);
    useEffect(() => {
        setPlacement(null); setCampaign(null); setAdError('');
        if (isLoading || !hasAccess) { setLoadingAd(false); return undefined; }
        let stopped = false;
        let unsubscribePlacement = () => {};
        let unsubscribeCampaign = () => {};
        let subscribedCampaign = '';
        const componentId = String(comp.id);
        const fail = () => { if (!stopped) { setCampaign(null); setPlacement(null); setLoadingAd(false); setAdError('Ad placement is unavailable.'); } };
        const attachPlacement = id => {
            unsubscribePlacement(); unsubscribeCampaign(); subscribedCampaign = ''; setCampaign(null); setPlacement(null);
            if (!id) { setLoadingAd(false); return; }
            unsubscribePlacement = onSnapshot(doc(db, AD_PLACEMENTS_COLLECTION, id), snapshot => {
                if (stopped) return;
                const item = snapshot.exists() ? { ...snapshot.data(), id: snapshot.id } : null;
                if (!item || item.schemaVersion !== 2 || item.creatorPublicKey !== ownerPublicKeyStr || String(item.componentId) !== componentId || (comp.campaignId && comp.campaignId !== item.campaignId)) { fail(); return; }
                setPlacement(item);
                if (subscribedCampaign === item.campaignId) return;
                unsubscribeCampaign(); subscribedCampaign = item.campaignId; setCampaign(null);
                unsubscribeCampaign = onSnapshot(doc(db, AD_CAMPAIGNS_COLLECTION, item.campaignId), campaignSnapshot => {
                    if (stopped) return;
                    const offer = campaignSnapshot.exists() ? { ...campaignSnapshot.data(), id: campaignSnapshot.id } : null;
                    setCampaign(offer?.advertiserPublicKey === item.advertiserPublicKey ? offer : null);
                    setLoadingAd(false);
                }, fail);
            }, fail);
        };
        setLoadingAd(true);
        let unsubscribeAssignment = () => {};
        if (comp.adPlacementId) attachPlacement(comp.adPlacementId);
        else unsubscribeAssignment = onSnapshot(query(collection(db, 'adPanelAssignments'), where('creatorPublicKey', '==', ownerPublicKeyStr), where('componentId', '==', componentId)), snapshot => {
            if (stopped) return;
            if (snapshot.size > 1) { fail(); return; }
            attachPlacement(snapshot.docs[0]?.data()?.placementId || '');
        }, fail);
        return () => { stopped = true; unsubscribeAssignment(); unsubscribePlacement(); unsubscribeCampaign(); };
    }, [comp.id, comp.adPlacementId, comp.campaignId, ownerPublicKeyStr, hasAccess, isLoading]);
    useEffect(() => {
        if (!campaign?.id || isEditor) return undefined;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [campaign?.id, isEditor]);
    useEffect(() => {
        if (!campaign?.id || !placement?.id || isEditor || !hasAccess || isLoading) return undefined;
        let stopped = false;
        const refresh = () => callAdMarketplace('refreshEscrow', { campaignId: campaign.id, placementId: placement.id }).catch(() => { if (!stopped) setNow(Date.now()); });
        refresh();
        const timer = setInterval(refresh, 60000);
        return () => { stopped = true; clearInterval(timer); };
    }, [campaign?.id, placement?.id, isEditor, hasAccess, isLoading]);
    const isPlacementActive = placement?.schemaVersion === 2 && isRenderableAdPlacementStatus(placement.status);
    const isCampaignActive = isAdCampaignServing(campaign, now, placement);
    const creative = campaign ? normalizeAdCreative(campaign) : null;
    const mediaAllowed = creative ? isAdMediumAllowed(creative.medium, comp.allowedMediaTypes) : false;
    const canRenderAd = !isLoading && !loadingAd && hasAccess && isCampaignActive && isPlacementActive && mediaAllowed;
    const basePanelClass = buildPanelClassName({ style });
    useEffect(() => { setAssetReady(!(creative?.assetUrl || creative?.imageUrl)); }, [campaign?.id, creative?.assetUrl, creative?.imageUrl]);
    const report = useCallback(type => {
        if (!canRenderAd || isEditor || !visible.current || document.hidden) return false;
        return recordAdEvent(placement, type).then(() => true, error => { console.warn('[ad event]', error?.code || 'unavailable'); return false; });
    }, [canRenderAd, isEditor, placement]);
    useEffect(() => {
        visible.current = false;
        if (!canRenderAd || isEditor || !assetReady || !adElement.current || typeof IntersectionObserver === 'undefined') return undefined;
        let intersecting = false;
        let started = 0;
        const update = () => {
            const next = intersecting && !document.hidden;
            if (next && !visible.current) started = Date.now();
            if (!next) started = 0;
            visible.current = next;
        };
        const observer = new IntersectionObserver(entries => { intersecting = entries[0]?.isIntersecting && entries[0].intersectionRatio >= 0.5; update(); }, { threshold: [0, 0.5, 1] });
        observer.observe(adElement.current);
        document.addEventListener('visibilitychange', update);
        const timer = setInterval(() => { if (visible.current && started && Date.now() - started >= 1000) report('impression'); }, 1000);
        return () => { visible.current = false; observer.disconnect(); clearInterval(timer); document.removeEventListener('visibilitychange', update); };
    }, [canRenderAd, isEditor, assetReady, report, adMounted]);

    const controls = (
        <ComponentControls
            compId={comp.id}
            index={index}
            moveUp={moveUp}
            moveDown={moveDown}
            onEdit={onEdit}
            removeComponent={removeComponent}
            gateType={gate.type}
            selectedListId={gate.listId}
            onGateTypeChange={onGateTypeChange}
            onGateListChange={onGateListChange}
            donationCampaignOptions={donationCampaignOptions}
            donationGate={donationGate ?? comp.gate}
            onDonationGateChange={onDonationGateChange}
            onDirectPaymentGateChange={onDirectPaymentGateChange}
            onDirectPaymentDraftChange={onDirectPaymentDraftChange}
            style={style}
            onStyleChange={onStyleChange}
            coverUrl={coverUrl}
            onCoverChange={onCoverChange}
            onPanelBackgroundImageChange={onPanelBackgroundImageChange}
            extraGates={extraGates ?? comp.extraGates ?? []}
            gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
            onExtraGatesChange={onExtraGatesChange}
            onGateLogicChange={onGateLogicChange}
        />
    );

    const analyticsFooter = isEditor && placement ? (
        <p style={{ fontSize: 12, opacity: 0.7 }}>
            Unverified ad activity: {placement.rawImpressions || 0} impressions, {placement.rawViews || 0} views, {placement.rawInteractions || 0} interactions. These counts do not trigger payouts.
        </p>
    ) : null;
    if (isLoading || loadingAd) return <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading || loadingAd} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
        <h3>{isLoading ? 'Checking access...' : 'Loading ad placement...'}</h3>{controls}
    </div>
        </GateRevealPanel>;
    if (!hasAccess) return <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading || loadingAd} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={buildPanelClassName({ style, extra: 'locked' })} style={style}>
        <h1>Access denied - {gateNamesLabel}</h1>{controls}
    </div>
        </GateRevealPanel>;
    return <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading || loadingAd} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>Sponsored</div>
        {comp.title ? <h2 style={{ marginTop: 0 }}>{comp.title}</h2> : null}
        {adError ? <p className="error">{adError}</p> : null}
        {!placement ? <p>This ad panel is not connected to a campaign yet.</p>
            : !campaign ? <p>The connected campaign is unavailable.</p>
            : !isPlacementActive ? <p>This placement is awaiting approval or has been paused.</p>
            : !isCampaignActive ? <p>This campaign is unavailable for funded delivery.</p>
            : !mediaAllowed ? <p>This campaign medium is not allowed for this panel.</p>
            : <div ref={attachAd}><AdCreativeRenderer campaign={campaign} onReady={ready} onAssetError={assetError} onInteraction={() => report('interaction')} onQualifiedView={() => report('view')} isVisible={() => visible.current} /></div>}
        <GateBadge gateNamesLabel={gateNamesLabel} onResetPasswordAccess={resetPasswordAccess} resetPasswordError={resetPasswordError} />
        {analyticsFooter}{controls}
    </div>
        </GateRevealPanel>;
}
